import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Search, Building2, X } from 'lucide-react';
import { API_BASE_URL } from '../../config/api';
import { slugify } from '../../utils/slugify';

// Enterprise option returned from search
interface EnterpriseOption {
  id: string;
  name: string;
  slug: string;
  status: string;
}

export interface Partner {
  id?: string;
  name: string;
  slug?: string;
  cpid?: string;
  type: 'reseller' | 'affiliate' | 'enterprise' | 'internal';
  status: 'active' | 'pending' | 'suspended' | 'cancelled';
  isOwnerPartner?: boolean;
  enterpriseId?: string | null;
  enterpriseName?: string;
  contact: {
    primaryName: string;
    primaryEmail: string;
    primaryPhone?: string;
    billingEmail?: string;
    technicalEmail?: string;
  };
  quotas: {
    maxOrganizations: number;
    maxUsersPerOrg: number;
    maxStorageGB: number;
  };
  features?: {
    whiteLabeling: boolean;
    customBranding: boolean;
    apiAccess: boolean;
    advancedAnalytics: boolean;
    prioritySupport: boolean;
    customIntegrations: boolean;
    multiRegion: boolean;
    sso: boolean;
  };
  stats?: {
    organizationCount: number;
    userCount: number;
  };
  createdAt?: string;
  isDeleted?: boolean;
}

export interface PartnerFormData {
  name: string;
  cpid: string;
  type: Partner['type'];
  status: Partner['status'];
  slug?: string;
  enterpriseId: string | null;
  contact: {
    primaryName: string;
    primaryEmail: string;
    primaryPhone: string;
    billingEmail: string;
    technicalEmail: string;
  };
  quotas: {
    maxOrganizations: number;
    maxUsersPerOrg: number;
    maxStorageGB: number;
  };
  features: {
    whiteLabeling: boolean;
    customBranding: boolean;
    apiAccess: boolean;
    advancedAnalytics: boolean;
    prioritySupport: boolean;
    customIntegrations: boolean;
    multiRegion: boolean;
    sso: boolean;
  };
}

interface PartnerModalProps {
  mode: 'create' | 'edit';
  partner?: Partner | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: PartnerFormData) => Promise<void>;
}

const emptyFeatures: PartnerFormData['features'] = {
  whiteLabeling: false,
  customBranding: false,
  apiAccess: false,
  advancedAnalytics: false,
  prioritySupport: false,
  customIntegrations: false,
  multiRegion: false,
  sso: false,
};

const emptyForm: PartnerFormData = {
  name: '',
  cpid: '',
  type: 'reseller',
  status: 'active',
  slug: '',
  enterpriseId: null,
  contact: {
    primaryName: '',
    primaryEmail: '',
    primaryPhone: '',
    billingEmail: '',
    technicalEmail: '',
  },
  quotas: {
    maxOrganizations: 10,
    maxUsersPerOrg: 50,
    maxStorageGB: 500,
  },
  features: { ...emptyFeatures },
};


/** A CPID is normally 5 digits; the length is not fixed, so only the ceiling is enforced. */
const CPID_MAX_DIGITS = 10;
const CPID_PATTERN = new RegExp(`^\\d{1,${CPID_MAX_DIGITS}}$`);

const featureLabels: Array<{ key: keyof PartnerFormData['features']; label: string }> = [
  { key: 'whiteLabeling', label: 'White Labeling' },
  { key: 'customBranding', label: 'Custom Branding' },
  { key: 'apiAccess', label: 'API Access' },
  { key: 'advancedAnalytics', label: 'Advanced Analytics' },
  { key: 'prioritySupport', label: 'Priority Support' },
  { key: 'customIntegrations', label: 'Custom Integrations' },
  { key: 'multiRegion', label: 'Multi-Region' },
  { key: 'sso', label: 'SSO (Single Sign-On)' },
];

export const PartnerModal: React.FC<PartnerModalProps> = ({
  mode,
  partner,
  open,
  onOpenChange,
  onSave,
}) => {
  const [formData, setFormData] = useState<PartnerFormData>(emptyForm);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('identity');
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Enterprise search state
  const [enterpriseSearch, setEnterpriseSearch] = useState('');
  const [enterpriseOptions, setEnterpriseOptions] = useState<EnterpriseOption[]>([]);
  const [selectedEnterprise, setSelectedEnterprise] = useState<EnterpriseOption | null>(null);
  const [isSearchingEnterprises, setIsSearchingEnterprises] = useState(false);
  const [showEnterpriseDropdown, setShowEnterpriseDropdown] = useState(false);

  // Ref for click-outside handling on enterprise dropdown
  const enterpriseDropdownRef = useRef<HTMLDivElement>(null);

  // Search enterprises with debounce — same pattern as OrganizationModal partner search
  const searchEnterprises = useCallback(async (query: string) => {
    setIsSearchingEnterprises(true);
    try {
      const token = localStorage.getItem('accessToken');
      const url = query
        ? `${API_BASE_URL}/v1/platform/enterprises?status=active&search=${encodeURIComponent(query)}&limit=10`
        : `${API_BASE_URL}/v1/platform/enterprises?status=active&limit=20`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        const enterprises: EnterpriseOption[] = (data.data?.enterprises || data.data || []).filter(
          (e: EnterpriseOption & { isDeleted?: boolean }) => !e.isDeleted
        );
        setEnterpriseOptions(enterprises);
      }
    } catch (err) {
      console.error('Failed to search enterprises:', err);
    } finally {
      setIsSearchingEnterprises(false);
    }
  }, []);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (showEnterpriseDropdown) {
        searchEnterprises(enterpriseSearch);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [enterpriseSearch, showEnterpriseDropdown, searchEnterprises]);

  // Click-outside handler for enterprise dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        enterpriseDropdownRef.current &&
        !enterpriseDropdownRef.current.contains(e.target as Node)
      ) {
        setShowEnterpriseDropdown(false);
      }
    };
    if (showEnterpriseDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showEnterpriseDropdown]);

  useEffect(() => {
    if (open) {
      setError(null);
      setValidationErrors({});
      setActiveTab('identity');
      setEnterpriseSearch('');
      setShowEnterpriseDropdown(false);

      if (partner) {
        setFormData({
          name: partner.name,
          cpid: partner.cpid || '',
          type: partner.type,
          status: partner.status,
          slug: partner.slug || '',
          enterpriseId: partner.enterpriseId || null,
          contact: {
            primaryName: partner.contact.primaryName,
            primaryEmail: partner.contact.primaryEmail,
            primaryPhone: partner.contact.primaryPhone || '',
            billingEmail: partner.contact.billingEmail || '',
            technicalEmail: partner.contact.technicalEmail || '',
          },
          quotas: {
            maxOrganizations: partner.quotas.maxOrganizations,
            maxUsersPerOrg: partner.quotas.maxUsersPerOrg,
            maxStorageGB: partner.quotas.maxStorageGB,
          },
          features: partner.features
            ? { ...emptyFeatures, ...partner.features }
            : { ...emptyFeatures },
        });

        // Restore selected enterprise from partner data
        if (partner.enterpriseId && partner.enterpriseName) {
          setSelectedEnterprise({
            id: partner.enterpriseId,
            name: partner.enterpriseName,
            slug: '',
            status: 'active',
          });
        } else {
          setSelectedEnterprise(null);
        }
      } else {
        setFormData({ ...emptyForm, features: { ...emptyFeatures } });
        setSelectedEnterprise(null);
      }
    }
  }, [open, partner]);

  const handleNameChange = (name: string) => {
    setFormData(prev => ({
      ...prev,
      name,
      slug: slugify(name),
    }));
    if (validationErrors.name) {
      setValidationErrors(prev => ({ ...prev, name: '' }));
    }
  };

  // Digits only, capped: anything else the user types or pastes never reaches the form.
  const handleCpidChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      cpid: value.replace(/\D/g, '').slice(0, CPID_MAX_DIGITS),
    }));
    if (validationErrors.cpid) {
      setValidationErrors(prev => ({ ...prev, cpid: '' }));
    }
  };

  const handleSelectEnterprise = (enterprise: EnterpriseOption) => {
    setSelectedEnterprise(enterprise);
    setFormData(prev => ({ ...prev, enterpriseId: enterprise.id }));
    setShowEnterpriseDropdown(false);
    setEnterpriseSearch('');
    if (validationErrors.enterpriseId) {
      setValidationErrors(prev => ({ ...prev, enterpriseId: '' }));
    }
  };

  const handleClearEnterprise = () => {
    setSelectedEnterprise(null);
    setFormData(prev => ({ ...prev, enterpriseId: null }));
  };

  const validate = (): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (!formData.name || formData.name.trim().length < 3) {
      errors.name = 'Name must be at least 3 characters';
    }
    // Optional, but a stored value that predates this rule still has to clear it.
    if (formData.cpid && !CPID_PATTERN.test(formData.cpid)) {
      errors.cpid = `CPID must be numbers only, up to ${CPID_MAX_DIGITS} digits`;
    }
    if (!formData.contact.primaryName || !formData.contact.primaryName.trim()) {
      errors.primaryName = 'Primary contact name is required';
    }
    if (!formData.contact.primaryEmail || !formData.contact.primaryEmail.trim()) {
      errors.primaryEmail = 'Primary contact email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact.primaryEmail)) {
      errors.primaryEmail = 'Invalid email format';
    }
    if (
      formData.contact.billingEmail &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact.billingEmail)
    ) {
      errors.billingEmail = 'Invalid billing email format';
    }
    if (
      formData.contact.technicalEmail &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact.technicalEmail)
    ) {
      errors.technicalEmail = 'Invalid technical email format';
    }

    setValidationErrors(errors);
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const errors = validate();
    if (Object.keys(errors).length > 0) {
      if (errors.name || errors.cpid) {
        setActiveTab('identity');
      } else if (
        errors.primaryName ||
        errors.primaryEmail ||
        errors.billingEmail ||
        errors.technicalEmail
      ) {
        setActiveTab('contact');
      }
      return;
    }

    setIsLoading(true);
    try {
      await onSave(formData);
      onOpenChange(false);
    } catch (err: unknown) {
      const axiosErr = err as any;
      const message = axiosErr?.response?.data?.error
        ?? (err instanceof Error ? err.message : 'Failed to save partner');
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogContent className="lg:max-w-3xl max-h-[90vh] p-0 grid grid-rows-[auto_auto_1fr_auto]">
        {/* Header */}
        <div className="border-b px-6 py-4">
          <DialogHeader>
            <DialogTitle>
              {mode === 'create' ? 'Create New Partner' : 'Edit Partner'}
            </DialogTitle>
          </DialogHeader>
        </div>

        <form onSubmit={handleSubmit} className="contents">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="contents">
            {/* Tab Navigation */}
            <div className="border-b px-6">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="identity">Identity</TabsTrigger>
                <TabsTrigger value="quotas">Quotas &amp; Features</TabsTrigger>
                <TabsTrigger value="contact">Contact</TabsTrigger>
                <TabsTrigger value="enterprise">Enterprise</TabsTrigger>
              </TabsList>
            </div>

            {/* Scrollable Content Area */}
            <div className="overflow-y-auto px-6 py-6">

              {/* Identity Tab */}
              <TabsContent value="identity" className="space-y-6 mt-0">
                <div className="space-y-5">
                  <h3 className="text-lg font-semibold text-gray-900 pb-3 border-b border-gray-200">
                    Partner Identity
                  </h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Partner Name *
                    </label>
                    <Input
                      value={formData.name}
                      onChange={(e) => handleNameChange(e.target.value)}
                      placeholder="Acme Health Partners"
                    />
                    {validationErrors.name && (
                      <p className="text-xs text-red-600 mt-1">{validationErrors.name}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1.5">
                      Slug preview:{' '}
                      <span className="font-mono bg-gray-100 px-1 rounded">
                        {formData.slug || '—'}
                      </span>
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      CPID
                    </label>
                    <Input
                      value={formData.cpid}
                      onChange={(e) => handleCpidChange(e.target.value)}
                      placeholder="12345"
                      inputMode="numeric"
                      maxLength={CPID_MAX_DIGITS}
                    />
                    {validationErrors.cpid && (
                      <p className="text-xs text-red-600 mt-1">{validationErrors.cpid}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1.5">
                      Channel Partner ID
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type *
                    </label>
                    <select
                      className="w-full px-3 py-2 border rounded-md"
                      value={formData.type}
                      onChange={(e) =>
                        setFormData({ ...formData, type: e.target.value as Partner['type'] })
                      }
                    >
                      <option value="reseller">Reseller</option>
                      <option value="affiliate">Affiliate</option>
                      <option value="enterprise">Enterprise</option>
                      <option value="internal">Internal</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status *
                    </label>
                    <select
                      className="w-full px-3 py-2 border rounded-md"
                      value={formData.status}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          status: e.target.value as Partner['status'],
                        })
                      }
                    >
                      <option value="active">Active</option>
                      <option value="pending">Pending</option>
                      <option value="suspended">Suspended</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>
              </TabsContent>

              {/* Quotas & Features Tab */}
              <TabsContent value="quotas" className="space-y-6 mt-0">
                {/* Quotas */}
                <div className="space-y-5">
                  <h3 className="text-lg font-semibold text-gray-900 pb-3 border-b border-gray-200">
                    Quotas
                  </h3>
                  <p className="text-sm text-gray-500">Use -1 for unlimited.</p>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Max Organizations
                      </label>
                      <Input
                        type="number"
                        value={formData.quotas.maxOrganizations}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            quotas: {
                              ...formData.quotas,
                              maxOrganizations: parseInt(e.target.value) || 0,
                            },
                          })
                        }
                        min={-1}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Max Users per Org
                      </label>
                      <Input
                        type="number"
                        value={formData.quotas.maxUsersPerOrg}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            quotas: {
                              ...formData.quotas,
                              maxUsersPerOrg: parseInt(e.target.value) || 0,
                            },
                          })
                        }
                        min={1}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Max Storage (GB)
                      </label>
                      <Input
                        type="number"
                        value={formData.quotas.maxStorageGB}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            quotas: {
                              ...formData.quotas,
                              maxStorageGB: parseInt(e.target.value) || 0,
                            },
                          })
                        }
                        min={-1}
                      />
                    </div>
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-5">
                  <h3 className="text-lg font-semibold text-gray-900 pb-3 border-b border-gray-200">
                    Features
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {featureLabels.map(({ key, label }) => (
                      <label key={key} className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.features[key]}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              features: { ...formData.features, [key]: e.target.checked },
                            })
                          }
                          className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <span className="text-sm text-gray-700">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </TabsContent>

              {/* Contact Tab */}
              <TabsContent value="contact" className="space-y-6 mt-0">
                <div className="space-y-5">
                  <h3 className="text-lg font-semibold text-gray-900 pb-3 border-b border-gray-200">
                    Contact Information
                  </h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Primary Contact Name *
                    </label>
                    <Input
                      value={formData.contact.primaryName}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          contact: { ...formData.contact, primaryName: e.target.value },
                        })
                      }
                      placeholder="Jane Smith"
                    />
                    {validationErrors.primaryName && (
                      <p className="text-xs text-red-600 mt-1">{validationErrors.primaryName}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Primary Contact Email *
                    </label>
                    <Input
                      type="email"
                      value={formData.contact.primaryEmail}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          contact: { ...formData.contact, primaryEmail: e.target.value },
                        })
                      }
                      placeholder="jane@example.com"
                    />
                    {validationErrors.primaryEmail && (
                      <p className="text-xs text-red-600 mt-1">{validationErrors.primaryEmail}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Primary Contact Phone
                    </label>
                    <Input
                      type="tel"
                      value={formData.contact.primaryPhone}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          contact: { ...formData.contact, primaryPhone: e.target.value },
                        })
                      }
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Billing Email
                    </label>
                    <Input
                      type="email"
                      value={formData.contact.billingEmail}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          contact: { ...formData.contact, billingEmail: e.target.value },
                        })
                      }
                      placeholder="billing@example.com"
                    />
                    {validationErrors.billingEmail && (
                      <p className="text-xs text-red-600 mt-1">{validationErrors.billingEmail}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Technical Email
                    </label>
                    <Input
                      type="email"
                      value={formData.contact.technicalEmail}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          contact: { ...formData.contact, technicalEmail: e.target.value },
                        })
                      }
                      placeholder="tech@example.com"
                    />
                    {validationErrors.technicalEmail && (
                      <p className="text-xs text-red-600 mt-1">
                        {validationErrors.technicalEmail}
                      </p>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Enterprise Tab */}
              <TabsContent value="enterprise" className="space-y-6 mt-0">
                <div className="space-y-5">
                  <h3 className="text-lg font-semibold text-gray-900 pb-3 border-b border-gray-200">
                    Enterprise Association
                  </h3>
                  <p className="text-sm text-gray-500">
                    Select the enterprise this partner belongs to, or leave independent.
                  </p>

                  {/* Enterprise selector */}
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Enterprise
                    </label>

                    {selectedEnterprise ? (
                      // Selected enterprise display
                      <div className="flex items-center justify-between px-3 py-2 border rounded-md bg-gray-50">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-gray-500" />
                          <span className="font-medium">{selectedEnterprise.name}</span>
                        </div>
                        <button
                          type="button"
                          onClick={handleClearEnterprise}
                          className="p-1 hover:bg-gray-200 rounded"
                        >
                          <X className="h-4 w-4 text-gray-500" />
                        </button>
                      </div>
                    ) : (
                      // Search input
                      <div className="relative" ref={enterpriseDropdownRef}>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <Input
                            type="text"
                            placeholder="Search enterprises..."
                            value={enterpriseSearch}
                            onChange={(e) => setEnterpriseSearch(e.target.value)}
                            onFocus={() => {
                              setShowEnterpriseDropdown(true);
                              searchEnterprises('');
                            }}
                            className="pl-9"
                          />
                        </div>

                        {/* Dropdown */}
                        {showEnterpriseDropdown && (
                          <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                            {/* Independent option */}
                            <button
                              type="button"
                              onClick={() => {
                                handleClearEnterprise();
                                setShowEnterpriseDropdown(false);
                                setEnterpriseSearch('');
                              }}
                              className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-2 border-b text-gray-500 italic"
                            >
                              <span>No Enterprise (Independent)</span>
                            </button>

                            {isSearchingEnterprises ? (
                              <div className="px-4 py-3 text-sm text-gray-500">Searching...</div>
                            ) : enterpriseOptions.length === 0 ? (
                              <div className="px-4 py-3 text-sm text-gray-500">
                                No enterprises found
                              </div>
                            ) : (
                              enterpriseOptions.map((enterprise) => (
                                <button
                                  key={enterprise.id}
                                  type="button"
                                  onClick={() => handleSelectEnterprise(enterprise)}
                                  className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center justify-between border-b last:border-b-0"
                                >
                                  <div className="flex items-center gap-2">
                                    <Building2 className="h-4 w-4 text-gray-400" />
                                    <span className="font-medium">{enterprise.name}</span>
                                  </div>
                                  <span className="text-xs text-gray-400">{enterprise.slug}</span>
                                </button>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {validationErrors.enterpriseId && (
                      <p className="text-xs text-red-600 mt-1">{validationErrors.enterpriseId}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1.5">
                      Independent partners are not associated with any enterprise.
                    </p>
                  </div>
                </div>
              </TabsContent>
            </div>
            {/* End of Scrollable Content Area */}
          </Tabs>

          {/* Footer */}
          <div className="border-t px-6 py-4">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                {error}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Saving...' : mode === 'create' ? 'Create Partner' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PartnerModal;
